import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { resolveIntendedPath } from "@/lib/authRedirect";
import { postLoginPathForUser } from "@shared/userRoles";
import { portalModuleAccessForUser } from "@shared/procurementInventory";
import { ROOT_REDIRECT_FLAG } from "@/contexts/PortalModeContext";
import { useSearch } from "wouter";

function safePostLoginRedirect(searchStr: string): string {
  const raw = new URLSearchParams(searchStr).get("redirect")?.trim() ?? "/";
  return resolveIntendedPath(raw || "/");
}

const LOGO_URL = "https://d2xsxph8kpxj0f.cloudfront.net/310519663414100320/brqZLwPGMDsYTB8zGXzCpe/clarity-logo_db6df1c5.jpg";

const ClarityLogo = ({ size = "header" }: { className?: string; size?: "header" | "large" }) => (
  <img
    src={LOGO_URL}
    alt="CLARITY"
    className={size === "large" ? "h-12 w-auto mx-auto block mb-4" : "h-7 w-auto"}
    style={{ objectFit: "contain" }}
  />
);

export default function Login() {
  const [step, setStep] = useState<"login" | "setup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [setupName, setSetupName] = useState("");
  const [setupEmail, setSetupEmail] = useState("");
  const [setupPassword, setSetupPassword] = useState("");
  const [setupPasswordConfirm, setSetupPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [step2, setStep2] = useState<"login" | "forgot" | "reset_sent">("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const loginSearch = useSearch();

  const { data: needsSetup } = trpc.auth.needsSetup.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (needsSetup) setStep("setup");
  }, [needsSetup]);

  const firstRunSetup = trpc.auth.firstRunSetup.useMutation({
    onSuccess: () => {
      toast.success("Admin account created. Please sign in.");
      setStep("login");
      setEmail(setupEmail);
      setPassword("");
    },
    onError: (err) => toast.error(err.message),
  });

  const requestReset = trpc.auth.requestPasswordReset.useMutation({
    onSuccess: () => {
      setStep2("reset_sent");
    },
    onError: (err) => toast.error(err.message || "Failed to send reset email."),
  });

  const login = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      const intended = safePostLoginRedirect(loginSearch);
      const access = portalModuleAccessForUser(data.user ?? null);
      const target = postLoginPathForUser(data.user ?? null, intended, (m) => access[m]);
      try {
        sessionStorage.setItem(ROOT_REDIRECT_FLAG, "1");
      } catch {
        // sessionStorage may be unavailable — non-fatal.
      }
      window.location.href = target;
    },
    onError: (err) => {
      toast.error(err.message || "Invalid email or password.");
      setPassword("");
    },
  });

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    login.mutate({ email: email.trim(), password });
  };

  const handleSetupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (setupPassword !== setupPasswordConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    firstRunSetup.mutate({ name: setupName, email: setupEmail, password: setupPassword });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Top bar */}
      <header className="border-b border-gray-100 px-8 py-5 flex items-center justify-between">
        <ClarityLogo size="header" />
        <span className="text-xs tracking-[0.2em] uppercase text-gray-400 font-medium">Service Portal</span>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-sm w-full">
          {step === "setup" ? (
            <div className="text-center">
              <ClarityLogo size="large" />
              <div className="w-8 h-px bg-black mx-auto mb-8" />
              <h1 className="text-2xl font-light tracking-wide text-black mb-2">Initial Setup</h1>
              <p className="text-sm text-gray-400 leading-relaxed mb-10">
                Create the first administrator account to get started.
              </p>
              <form onSubmit={handleSetupSubmit} className="space-y-4 text-left">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Your Name</label>
                  <input
                    type="text"
                    value={setupName}
                    onChange={e => setSetupName(e.target.value)}
                    placeholder="e.g. Soon"
                    required
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={setupEmail}
                    onChange={e => setSetupEmail(e.target.value)}
                    placeholder="you@clarity.com"
                    required
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Password</label>
                  <input
                    type="password"
                    value={setupPassword}
                    onChange={e => setSetupPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    required
                    minLength={6}
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Confirm Password</label>
                  <input
                    type="password"
                    value={setupPasswordConfirm}
                    onChange={e => setSetupPasswordConfirm(e.target.value)}
                    placeholder="Repeat password"
                    required
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={firstRunSetup.isPending || !setupName.trim() || !setupEmail.trim() || !setupPassword || !setupPasswordConfirm}
                  className="w-full bg-black text-white h-12 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {firstRunSetup.isPending ? "Creating…" : "Create Admin Account"}
                </button>
              </form>
            </div>
          ) : step2 === "forgot" ? (
            <div className="text-center">
              <ClarityLogo size="large" />
              <div className="w-8 h-px bg-black mx-auto mb-8" />
              <h1 className="text-2xl font-light tracking-wide text-black mb-2">Reset Password</h1>
              <p className="text-sm text-gray-400 leading-relaxed mb-10">
                Enter your email address and we will send you a reset link.
              </p>
              <form onSubmit={(e) => { e.preventDefault(); requestReset.mutate({ email: forgotEmail.trim() }); }} className="space-y-4 text-left">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={requestReset.isPending || !forgotEmail.trim()}
                  className="w-full bg-black text-white h-12 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {requestReset.isPending ? "Sending…" : "Send Reset Link"}
                </button>
              </form>
              <button
                type="button"
                onClick={() => setStep2("login")}
                className="mt-6 text-xs text-gray-400 hover:text-black transition-colors"
              >
                ← Back to sign in
              </button>
            </div>
          ) : step2 === "reset_sent" ? (
            <div className="text-center">
               <ClarityLogo size="large" />
              <div className="w-8 h-px bg-black mx-auto mb-8" />
              <h1 className="text-2xl font-light tracking-wide text-black mb-2">Forgot Password</h1>
              <p className="text-sm text-gray-400 leading-relaxed mb-10">
                If an account exists for <strong className="text-black">{forgotEmail}</strong>, a password reset link has been sent. The link expires in 1 hour.
              </p>
              <button
                type="button"
                onClick={() => { setStep2("login"); setForgotEmail(""); }}
                className="w-full bg-black text-white h-12 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <div className="text-center">
              <ClarityLogo size="large" />
              <div className="w-8 h-px bg-black mx-auto mb-8" />
              <h1 className="text-2xl font-light tracking-wide text-black mb-2">Sign In</h1>
              <p className="text-sm text-gray-400 leading-relaxed mb-10">
                Enter your email and password to access the portal.
              </p>
              <form onSubmit={handleLoginSubmit} className="space-y-4 text-left">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none"
                    autoFocus
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Your password"
                      required
                      className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none pr-12"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors text-xs"
                      tabIndex={-1}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={login.isPending || !email.trim() || !password}
                  className="w-full bg-black text-white h-12 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {login.isPending ? "Signing in…" : "Sign In"}
                </button>
              </form>
              <button
                type="button"
                onClick={() => setStep2("forgot")}
                className="mt-4 text-xs text-gray-400 hover:text-black transition-colors underline underline-offset-2"
              >
                Forgot password?
              </button>
              <p className="text-xs text-gray-300 mt-4 tracking-wide">Authorised personnel only</p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 px-8 py-4 flex items-center justify-between">
        <p className="text-xs text-gray-300">&copy; {new Date().getFullYear()} Clarity. All rights reserved.</p>
        <p className="text-xs text-gray-300 tracking-[0.15em] uppercase">Clarity®</p>
      </footer>
    </div>
  );
}
